/**
 * Projects Page
 * Phase 4: Projects & Audit Runs
 * 
 * Lists all projects with summary stats, latest audit info, and filters.
 * Provides navigation to project audits and audit history.
 */

import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api, ProjectListItem } from '../lib/api'
import { LoadingSpinner } from '../components/design-system/Loading'
import { EmptyState } from '../components/design-system/EmptyState'
import { Badge, StatusBadge, SeverityBadge } from '../components/design-system/Badge'
import { Button } from '../components/design-system/Button'
import { CreateProjectDialog } from '../components/CreateProjectDialog'
import { StartAuditDialog } from '../components/StartAuditDialog'
import { formatRelativeTime } from '../lib/dashboardHelpers'
import { useLocale } from '../contexts/LocaleContext'

type SortField = 'name' | 'last_audit' | 'audit_count' | 'risk'
type SortOrder = 'asc' | 'desc'

export function ProjectsPage() {
  const { t, locale } = useLocale()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [projects, setProjects] = useState<ProjectListItem[]>([])
  
  // Filters and sorting from URL
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '')
  const [sourceTypeFilter, setSourceTypeFilter] = useState(searchParams.get('source_type') || '')
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '')
  const [sortField, setSortField] = useState<SortField>((searchParams.get('sort') as SortField) || 'last_audit')
  const [sortOrder, setSortOrder] = useState<SortOrder>((searchParams.get('order') as SortOrder) || 'desc')
  const [createOpen, setCreateOpen] = useState(false)
  const [startOpen, setStartOpen] = useState(false)
  const [startProjectId, setStartProjectId] = useState<string | undefined>()
  
  useEffect(() => {
    loadProjects()
  }, [])
  
  // Update URL when filters change
  useEffect(() => {
    const params: Record<string, string> = {}
    if (searchQuery) params.search = searchQuery
    if (sourceTypeFilter) params.source_type = sourceTypeFilter
    if (statusFilter) params.status = statusFilter
    if (sortField !== 'last_audit') params.sort = sortField
    if (sortOrder !== 'desc') params.order = sortOrder
    
    setSearchParams(params)
  }, [searchQuery, sourceTypeFilter, statusFilter, sortField, sortOrder, setSearchParams])
  
  async function loadProjects() {
    try {
      setLoading(true)
      setError(null)
      
      const data = await api.listProjects()
      setProjects(data.projects)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('projects.failed'))
    } finally {
      setLoading(false)
    }
  }
  
  // Client-side filtering and sorting
  const filteredAndSortedProjects = useMemo(() => {
    let result = [...projects]
    
    // Text search
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      result = result.filter(p => 
        p.display_name.toLowerCase().includes(query) ||
        (p.description?.toLowerCase().includes(query)) ||
        (p.external_id?.toLowerCase().includes(query))
      )
    }
    
    // Source type filter
    if (sourceTypeFilter) {
      result = result.filter(p => p.source_type === sourceTypeFilter)
    }
    
    // Status filter (based on latest audit status)
    if (statusFilter) {
      result = result.filter(p => p.latest_audit_status === statusFilter)
    }
    
    // Sorting
    result.sort((a, b) => {
      let comparison = 0
      
      switch (sortField) {
        case 'name':
          comparison = a.display_name.localeCompare(b.display_name)
          break
        case 'last_audit':
          // Sort by last_audit_at, nulls last
          if (!a.last_audit_at && !b.last_audit_at) comparison = 0
          else if (!a.last_audit_at) comparison = 1
          else if (!b.last_audit_at) comparison = -1
          else comparison = a.last_audit_at.localeCompare(b.last_audit_at)
          break
        case 'audit_count':
          comparison = a.audit_count - b.audit_count
          break
        case 'risk':
          // Sort by critical + high findings
          const aRisk = a.latest_audit_critical_count + a.latest_audit_high_count
          const bRisk = b.latest_audit_critical_count + b.latest_audit_high_count
          comparison = aRisk - bRisk
          break
      }
      
      return sortOrder === 'asc' ? comparison : -comparison
    })
    
    return result
  }, [projects, searchQuery, sourceTypeFilter, statusFilter, sortField, sortOrder])
  
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortOrder('desc')
    }
  }
  
  const uniqueSourceTypes = useMemo(() => {
    const types = new Set(projects.map(p => p.source_type))
    return Array.from(types).sort()
  }, [projects])
  
  const uniqueStatuses = useMemo(() => {
    const statuses = new Set(
      projects
        .map(p => p.latest_audit_status)
        .filter((s): s is string => s !== null)
    )
    return Array.from(statuses).sort()
  }, [projects])
  
  // Loading state
  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <h1 style={{
          margin: 0,
          fontFamily: 'var(--reviewer-font-mono)',
          fontSize: 'var(--reviewer-text-2xl)',
          fontWeight: 500,
          color: 'var(--reviewer-text)',
        }}>
          {t('nav.projects')}
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '320px' }}>
          <LoadingSpinner size="lg" text={t('projects.loading')} />
        </div>
      </div>
    )
  }
  
  // Error state
  if (error) {
    return (
      <div
        style={{
          maxWidth: '600px',
          margin: '0 auto',
          padding: '32px',
        }}
      >
        <div
          style={{
            border: '1px solid var(--reviewer-danger-border)',
            backgroundColor: 'var(--reviewer-danger-soft)',
            padding: '24px',
          }}
        >
          <h2 style={{ 
            margin: 0, 
            marginBottom: '12px',
            fontFamily: 'var(--reviewer-font-mono)',
            fontSize: 'var(--reviewer-text-lg)',
            color: 'var(--reviewer-danger)',
          }}>
            {t('projects.failed')}
          </h2>
          <p style={{ 
            margin: 0, 
            marginBottom: '16px',
            fontFamily: 'var(--reviewer-font-mono)',
            fontSize: 'var(--reviewer-text-sm)',
            color: 'var(--reviewer-text)',
          }}>
            {error}
          </p>
          <Button onClick={loadProjects} variant="danger">
            {t('common.retry')}
          </Button>
        </div>
      </div>
    )
  }
  
  // Empty state
  if (projects.length === 0) {
    return (
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '32px 0' }} data-testid="projects-page">
        <h1 style={{
          margin: 0,
          marginBottom: '16px',
          fontFamily: 'var(--reviewer-font-mono)',
          fontSize: 'var(--reviewer-text-2xl)',
          fontWeight: 500,
          color: 'var(--reviewer-text)',
          textAlign: 'center',
        }}>
          {t('nav.projects')}
        </h1>
        <EmptyState 
          title={t('projects.empty')} 
          description={t('projects.emptyBody')}
          action={{ label: t('projects.add'), onClick: () => setCreateOpen(true) }}
        />
        <CreateProjectDialog
          isOpen={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={async (projectId) => {
            await loadProjects()
            setStartProjectId(projectId)
            setStartOpen(true)
          }}
        />
        <StartAuditDialog
          isOpen={startOpen}
          onClose={() => setStartOpen(false)}
          initialProjectId={startProjectId}
        />
      </div>
    )
  }
  
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }} data-testid="projects-page">
      {/* Header */}
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
        <div>
          <h1
            style={{
              margin: 0,
              marginBottom: '8px',
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-2xl)',
              fontWeight: 500,
              color: 'var(--reviewer-text)',
            }}
          >
            {t('nav.projects')}
          </h1>
          <p
            style={{
              margin: 0,
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-base)',
              color: 'var(--reviewer-text-secondary)',
            }}
          >
            {t('projects.shown', { n: projects.length, shown: filteredAndSortedProjects.length, plural: projects.length !== 1 ? 's' : '' })}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button variant="secondary" onClick={() => setStartOpen(true)}>
            {t('projects.startAudit')}
          </Button>
          <Button onClick={() => setCreateOpen(true)} data-testid="add-project-button">
            {t('projects.add')}
          </Button>
        </div>
      </header>

      {/* Filters */}
      <div
        style={{
          display: 'flex',
          gap: '12px',
          flexWrap: 'wrap',
          alignItems: 'center',
          padding: '16px',
          border: '1px solid var(--reviewer-border)',
          backgroundColor: 'var(--reviewer-surface)',
        }}
      >
        {/* Search */}
        <input
          type="text"
          placeholder={t('projects.searchPh')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            flex: '1 1 300px',
            padding: '8px 12px',
            fontFamily: 'var(--reviewer-font-mono)',
            fontSize: 'var(--reviewer-text-sm)',
            backgroundColor: 'var(--reviewer-bg)',
            border: '1px solid var(--reviewer-border)',
            color: 'var(--reviewer-text)',
            outline: 'none',
          }}
          aria-label={t('projects.searchPh')}
        />
        
        {/* Source Type Filter */}
        {uniqueSourceTypes.length > 1 && (
          <select
            value={sourceTypeFilter}
            onChange={(e) => setSourceTypeFilter(e.target.value)}
            style={{
              padding: '8px 12px',
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-sm)',
              backgroundColor: 'var(--reviewer-bg)',
              border: '1px solid var(--reviewer-border)',
              color: 'var(--reviewer-text)',
              outline: 'none',
            }}
            aria-label={t('projects.filterSourceAria')}
          >
            <option value="">{t('projects.allSources')}</option>
            {uniqueSourceTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        )}
        
        {/* Status Filter */}
        {uniqueStatuses.length > 0 && (
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: '8px 12px',
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-sm)',
              backgroundColor: 'var(--reviewer-bg)',
              border: '1px solid var(--reviewer-border)',
              color: 'var(--reviewer-text)',
              outline: 'none',
            }}
            aria-label={t('projects.filterStatusAria')}
          >
            <option value="">{t('projects.allStatuses')}</option>
            {uniqueStatuses.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        )}
        
        {/* Clear Filters */}
        {(searchQuery || sourceTypeFilter || statusFilter) && (
          <button
            onClick={() => {
              setSearchQuery('')
              setSourceTypeFilter('')
              setStatusFilter('')
            }}
            style={{
              padding: '8px 12px',
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-sm)',
              backgroundColor: 'transparent',
              border: '1px solid var(--reviewer-border)',
              color: 'var(--reviewer-text-secondary)',
              cursor: 'pointer',
            }}
            aria-label={t('projects.clearAria')}
          >
            {t('projects.clearFilters')}
          </button>
        )}
      </div>

      {/* Projects Table */}
      {filteredAndSortedProjects.length === 0 ? (
        <div style={{ padding: '48px 0', textAlign: 'center' }}>
          <p style={{ 
            margin: 0,
            fontFamily: 'var(--reviewer-font-mono)',
            fontSize: 'var(--reviewer-text-base)',
            color: 'var(--reviewer-text-secondary)',
          }}>
            {t('projects.noMatch')}
          </p>
        </div>
      ) : (
        <div
          style={{
            border: '1px solid var(--reviewer-border)',
            backgroundColor: 'var(--reviewer-surface)',
            overflowX: 'auto',
          }}
        >
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-sm)',
            }}
            role="table"
            aria-label={t('projects.tableAria')}
          >
            <thead>
              <tr style={{ borderBottom: '1px solid var(--reviewer-border)' }}>
                <th
                  onClick={() => handleSort('name')}
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontWeight: 500,
                    color: 'var(--reviewer-text-secondary)',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                  role="columnheader"
                  aria-sort={sortField === 'name' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
                >
                  {t('projects.colProject')} {sortField === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontWeight: 500,
                    color: 'var(--reviewer-text-secondary)',
                  }}
                  role="columnheader"
                >
                  {t('projects.colSource')}
                </th>
                <th
                  onClick={() => handleSort('last_audit')}
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontWeight: 500,
                    color: 'var(--reviewer-text-secondary)',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                  role="columnheader"
                  aria-sort={sortField === 'last_audit' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
                >
                  {t('projects.colLastAudit')} {sortField === 'last_audit' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  onClick={() => handleSort('audit_count')}
                  style={{
                    padding: '12px 16px',
                    textAlign: 'center',
                    fontWeight: 500,
                    color: 'var(--reviewer-text-secondary)',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                  role="columnheader"
                  aria-sort={sortField === 'audit_count' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
                >
                  {t('projects.colAudits')} {sortField === 'audit_count' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontWeight: 500,
                    color: 'var(--reviewer-text-secondary)',
                  }}
                  role="columnheader"
                >
                  {t('projects.colStatus')}
                </th>
                <th
                  onClick={() => handleSort('risk')}
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontWeight: 500,
                    color: 'var(--reviewer-text-secondary)',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                  role="columnheader"
                  aria-sort={sortField === 'risk' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
                >
                  {t('projects.colRisk')} {sortField === 'risk' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'right',
                    fontWeight: 500,
                    color: 'var(--reviewer-text-secondary)',
                  }}
                  role="columnheader"
                >
                  {t('projects.colActions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedProjects.map((project) => (
                <tr
                  key={project.id}
                  style={{
                    borderBottom: '1px solid var(--reviewer-border-subtle)',
                    cursor: 'pointer',
                    transition: 'background-color 0.15s',
                  }}
                  onClick={() => {
                    if (project.latest_audit_id) {
                      navigate(`/audit/${project.latest_audit_id}`)
                    }
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--reviewer-bg-elevated)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent'
                  }}
                  role="row"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && project.latest_audit_id) {
                      navigate(`/audit/${project.latest_audit_id}`)
                    }
                  }}
                  aria-label={t('projects.rowAria', { name: project.display_name, n: project.audit_count, status: project.latest_audit_status || t('projects.noneStatus') })}
                >
                  <td style={{ padding: '12px 16px' }}>
                    <div>
                      <div style={{ 
                        fontWeight: 500,
                        color: 'var(--reviewer-text)',
                        marginBottom: '4px',
                      }}>
                        {project.display_name}
                      </div>
                      {project.description && (
                        <div style={{ 
                          fontSize: 'var(--reviewer-text-xs)',
                          color: 'var(--reviewer-text-tertiary)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '300px',
                        }}>
                          {project.description}
                        </div>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <Badge variant="default" size="sm">
                      {project.source_type}
                    </Badge>
                  </td>
                  <td style={{ padding: '12px 16px', color: 'var(--reviewer-text-secondary)' }}>
                    {project.last_audit_at ? formatRelativeTime(project.last_audit_at, locale) : t('projects.never')}
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'center', color: 'var(--reviewer-text)' }}>
                    {project.audit_count}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {project.latest_audit_status ? (
                      <StatusBadge status={project.latest_audit_status} size="sm" />
                    ) : (
                      <span style={{ color: 'var(--reviewer-text-tertiary)' }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      {project.latest_audit_critical_count > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <SeverityBadge severity="critical" size="sm" />
                          <span style={{ fontSize: 'var(--reviewer-text-xs)', color: 'var(--reviewer-danger)' }}>
                            {project.latest_audit_critical_count}
                          </span>
                        </div>
                      )}
                      {project.latest_audit_high_count > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <SeverityBadge severity="high" size="sm" />
                          <span style={{ fontSize: 'var(--reviewer-text-xs)', color: 'var(--reviewer-warning)' }}>
                            {project.latest_audit_high_count}
                          </span>
                        </div>
                      )}
                      {project.latest_audit_critical_count === 0 && project.latest_audit_high_count === 0 && project.latest_audit_total_findings > 0 && (
                        <span style={{ fontSize: 'var(--reviewer-text-xs)', color: 'var(--reviewer-text-secondary)' }}>
                          {project.latest_audit_total_findings} finding{project.latest_audit_total_findings !== 1 ? 's' : ''}
                        </span>
                      )}
                      {project.latest_audit_total_findings === 0 && project.latest_audit_id && (
                        <span style={{ fontSize: 'var(--reviewer-text-xs)', color: 'var(--reviewer-success)' }}>
                          Clean
                        </span>
                      )}
                      {!project.latest_audit_id && (
                        <span style={{ fontSize: 'var(--reviewer-text-xs)', color: 'var(--reviewer-text-tertiary)' }}>
                          —
                        </span>
                      )}
                    </div>
                  </td>
                  <td 
                    style={{ padding: '12px 16px', textAlign: 'right' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {project.latest_audit_id && (
                      <button
                        onClick={() => navigate(`/audit/${project.latest_audit_id}`)}
                        style={{
                          padding: '4px 12px',
                          fontFamily: 'var(--reviewer-font-mono)',
                          fontSize: 'var(--reviewer-text-xs)',
                          backgroundColor: 'transparent',
                          border: '1px solid var(--reviewer-accent)',
                          color: 'var(--reviewer-accent)',
                          cursor: 'pointer',
                        }}
                        aria-label={`View latest audit for ${project.display_name}`}
                      >
                        View Audit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <CreateProjectDialog
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={async (projectId) => {
          await loadProjects()
          setStartProjectId(projectId)
          setStartOpen(true)
        }}
      />
      <StartAuditDialog
        isOpen={startOpen}
        onClose={() => setStartOpen(false)}
        initialProjectId={startProjectId}
      />
    </div>
  )
}
